import { useMemo } from "react";

import { qrMatrix } from "@/lib/qrcode/qrcode";

/** Modules of white border around the code (the QR specification asks for 4). */
const QUIET_ZONE = 4;

const matrixOf = (value: string): boolean[][] | null => {
  try {
    return qrMatrix(value);
  } catch {
    // Text too long for the largest version: show no code rather than a wrong one.
    return null;
  }
};

/**
 * Almikia Epic EQ2 (TASK-101): a QR code drawn as one SVG path from the
 * vendored generator (`src/lib/qrcode/`). Prints crisp at any size and needs
 * no canvas, image file or network.
 */
export const QrCode = ({
  value,
  label,
  className,
}: {
  value: string;
  /** Accessible name (the QR is also announced as an image). */
  label: string;
  className?: string;
}) => {
  const matrix = useMemo(() => matrixOf(value), [value]);
  if (!matrix) return null;

  const size = matrix.length + QUIET_ZONE * 2;
  const path = matrix
    .flatMap((row, r) =>
      row.map((dark, c) =>
        dark ? `M${c + QUIET_ZONE},${r + QUIET_ZONE}h1v1h-1z` : "",
      ),
    )
    .join("");

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      className={className}
      data-testid="advisor-qr"
    >
      <rect width={size} height={size} fill="#ffffff" />
      <path d={path} fill="#20244a" />
    </svg>
  );
};
